import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  userSelector,
  profesorCoursesSelector,
  studentCoursesSelector,
  isGoogleSyncedSelector,
  timezoneSelector,
  googleStatusSelector
} from './store/selector';
import { getGoogleStatusSuccessApiAction } from './store/reducer';
import { meApiService } from './services/api';
import {
  getMeCoursesFailureApiAction,
  getMeCoursesInitApiAction,
  getMeCoursesSuccessApiAction,
  getMeUserFailureApiAction,
  getMeUserInitApiAction,
  getMeUserSuccessApiAction,
  meClearAction,
  putMePasswordFailureApiAction,
  putMePasswordInitApiAction,
  putMePasswordSuccessApiAction,
  putMeUserFailureApiAction,
  putMeUserInitApiAction,
  putMeUserSuccessApiAction,
  setTimezoneAction,
} from './store/reducer';
import { PutMeUserPayloadRequest } from './interfaces/put-me-user';
import { PutMePasswordPayloadRequest } from './interfaces/put-me-password';
import { toast } from 'react-toastify';
import { GetMeUserResponse } from './interfaces/get-me-user';
import { getRequestErrorCode } from '../../utils/utils';

export function useMeCore() {
  const user = useSelector(userSelector);
  const profesorCourses = useSelector(profesorCoursesSelector);
  const studentCourses = useSelector(studentCoursesSelector);
  const isGoogleSynced = useSelector(isGoogleSyncedSelector);
  const timezone = useSelector(timezoneSelector);
  const errorCode = useSelector(errorCodeSelector);
  const googleStatus = useSelector(googleStatusSelector);
  const dispatch = useDispatch();

  const getMeUser = useCallback(
    async (onSuccess?: (arg0: GetMeUserResponse) => void, onFailure?: () => void) => {
      try {
        dispatch(getMeUserInitApiAction());
        const response = await meApiService.getMeUser();
        dispatch(getMeUserSuccessApiAction(response));
        onSuccess?.(response);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getMeUserFailureApiAction({ errorCode: getRequestErrorCode(error) })
        );
      }
    },
    [dispatch]
  );

  const putMeUser = useCallback(
    async (body: PutMeUserPayloadRequest, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(putMeUserInitApiAction());
        await meApiService.putMeUser(body);
        dispatch(putMeUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        if (error.request.status === 413) {
          toast.warn("El tamaño máximo de archivos permitidos es de 25MB!")
        }
        onFailure?.();
        dispatch(
          putMeUserFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putMePassword = useCallback(
    async (body: PutMePasswordPayloadRequest, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(putMePasswordInitApiAction());
        await meApiService.putMePassword(body);
        dispatch(putMePasswordSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putMePasswordFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getMeCourses = useCallback(
    async (onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(getMeCoursesInitApiAction());
        const response = await meApiService.getMeCourses();
        dispatch(getMeCoursesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getMeCoursesFailureApiAction({ errorCode: getRequestErrorCode(error) })
        );
      }
    },
    [dispatch]
  );

  const getGoogleSync = useCallback(
    async (onSuccess?: () => void, onFailure?: () => void) => {
      try {
        const response = await meApiService.getGoogleSync();
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
      }
    },
    [dispatch]
  );

  const getGoogleStatus = useCallback(
    async (onSuccess?: (arg0: any) => void, onFailure?: () => void) => {
      try {
        const response = await meApiService.getGoogleStatus();
        dispatch(getGoogleStatusSuccessApiAction(response));
        onSuccess?.(response);
      } catch (error: any) {
        // Manejo de error
        onFailure?.();
      }
    },
    [dispatch]
  );

  const setTimezone = useCallback((timeZone: string) => {
    dispatch(setTimezoneAction(timeZone));
  }, [dispatch]);

  const clearMe = useCallback(() => {
    dispatch(meClearAction());
  }, [dispatch]);

  return {
    user,
    profesorCourses,
    studentCourses,
    isGoogleSynced,
    timezone,
    errorCode,
    googleStatus,
    getMeUser,
    putMeUser,
    getMeCourses,
    putMePassword,
    getGoogleSync,
    getGoogleStatus,
    setTimezone,
    clearMe,
  };
}
